import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Calendar, Clock, Users, Search, X, LogIn, User, CalendarDays, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import AvatarStack from "@/components/AvatarStack";
import TopNav from "@/components/TopNav";
import LeaguesRow from "@/components/LeaguesRow";
import TripsRow from "@/components/TripsRow";
import { eventCalendarDate } from "@/lib/dates";

const SPORTS = [
  { id: "all", label: "All Sports", icon: "🏃" },
  { id: "basketball", label: "Basketball", icon: "🏀" },
  { id: "football", label: "Football", icon: "⚽" },
  { id: "padel", label: "Padel", icon: "🎾" },
  { id: "volleyball", label: "Volleyball", icon: "🏐" }
];

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [games, setGames] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedSport, setSelectedSport] = useState("all");
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [eventType, setEventType] = useState("all"); // "all" | "single" | "series"
  const [userLocation, setUserLocation] = useState(null);

  // Fetch user location (for UI indicator only, not for filtering)
  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        () => {},
        { timeout: 5000 }
      );
    }
  }, []);

  // Fetch cities
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await fetch(`${API}/cities`);
        if (response.ok) {
          const data = await response.json();
          setCities(data.cities || []);
        }
      } catch (error) {
        console.error("Failed to fetch cities:", error);
      }
    };
    fetchCities();
  }, []);

  // Fetch games (with localStorage cache, retry, and graceful fallback)
  const fetchGames = useCallback(async () => {
    const params = new URLSearchParams();
    if (selectedSport && selectedSport !== "all") params.append("sport", selectedSport);
    if (selectedCity) params.append("city", selectedCity);
    const cacheKey = `pouls:games:${params.toString()}`;

    // Show cached games immediately if available - no spinner flash on repeat visits
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setGames(parsed);
          setLoading(false);
        }
      }
    } catch (e) {
      // ignore cache errors
    }

    if (games.length === 0) setLoading(true);

    const fetchOnce = async (timeoutMs) => {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), timeoutMs);
      try {
        const r = await fetch(`${API}/games-with-attendees?${params.toString()}`, { signal: ctrl.signal });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return await r.json();
      } finally {
        clearTimeout(timer);
      }
    };

    try {
      // First attempt: 8s
      let data;
      try {
        data = await fetchOnce(8000);
      } catch (e1) {
        console.warn("[games] first attempt failed, retrying:", e1?.message || e1);
        // Cold-start fallback: longer timeout
        data = await fetchOnce(25000);
      }
      setGames(data);
      try { sessionStorage.setItem(cacheKey, JSON.stringify(data)); } catch (e) {}
    } catch (error) {
      console.error("Failed to fetch games:", error);
      // Only show error if we have nothing cached to display
      if (games.length === 0) {
        toast.error("Connection slow — please try again");
      }
    } finally {
      setLoading(false);
    }
  }, [selectedSport, selectedCity, games.length]);

  // Background warm-up ping (don't await, helps wake up cold backends)
  useEffect(() => {
    fetch(`${API}/`).catch(() => {});
  }, []);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  const formatDate = (dateString) => {
    const date = eventCalendarDate(dateString);
    if (!date) return "";
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === now.toDateString()) {
      return "Today";
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return "Tomorrow";
    }
    return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  };

  const UK_CITIES = ["London", "Manchester", "Birmingham", "Liverpool", "Edinburgh", "Leeds", "Glasgow", "Bristol"];
  const CITY_FLAGS = { Berlin: "🇩🇪", Amsterdam: "🇳🇱", Paris: "🇫🇷" };
  
  const getCityWithFlag = (city) => {
    if (CITY_FLAGS[city]) return `${CITY_FLAGS[city]} ${city}`;
    const isUK = UK_CITIES.includes(city);
    const flag = isUK ? "🇬🇧" : "🇺🇸";
    return `${flag} ${city}`;
  };

  const getSportBadgeClass = (sport) => {
    switch (sport) {
      case "basketball": return "sport-badge-basketball";
      case "football": return "sport-badge-football";
      case "padel": return "sport-badge-padel";
      case "volleyball": return "sport-badge-volleyball";
      default: return "bg-primary/20 text-primary";
    }
  };

  const spotsLeft = (game) => game.max_players - game.current_players;

  const formatPrice = (price, currency = "USD") => {
    if (price === 0) return "Free";
    const symbol = currency === "GBP" ? "£" : currency === "EUR" ? "€" : "$";
    return `${symbol}${price}`;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="home-page">
      <TopNav />

      {/* Hero — full-bleed image card with badge, headline & search bar */}
      <section className="container mx-auto px-4 pt-6 pb-8" id="games-grid">
        <div className="relative rounded-3xl overflow-hidden min-h-[70vh] bg-card shadow-sm">
          {/* Background image */}
          <img
            src="https://images.unsplash.com/photo-1546519638-68e109498ffc?w=1920&q=80&auto=format&fit=crop"
            alt="Stadium lights"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Dark gradient overlay for legibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/10" />

          {/* Content */}
          <div className="relative z-10 flex flex-col h-full min-h-[70vh] px-6 sm:px-12 py-10 sm:py-14">
            {/* Top badge */}
            <div className="flex">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-medium shadow-md">
                <span className="w-1.5 h-1.5 rounded-full bg-foreground/80 animate-pulse" />
                New games every day
              </span>
            </div>

            {/* Spacer pushes copy + search bar to the bottom */}
            <div className="flex-1" />

            {/* Headline */}
            <h2 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-3 max-w-3xl">
              The Home Of The <span className="text-primary italic">Modern Athlete.</span>
            </h2>

            {/* Sub copy */}
            <p className="text-white/80 text-base sm:text-lg max-w-xl mb-8">
              Social sports with people in your city. Reserve your spot now.
            </p>

            {/* Unified search card: tab strip + filters + submit, all on one white surface */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-4xl" data-testid="hero-search-card">
              {/* Event-type tabs — top strip of the search card */}
              <div
                className="overflow-x-auto bg-muted/30 border-b border-border"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                data-testid="event-type-tabs"
              >
                <style>{`[data-testid="event-type-tabs"]::-webkit-scrollbar { display: none; }`}</style>
                <div className="flex items-center gap-1 px-2 py-1.5 min-w-max">
                  {[
                    { id: "all", label: "All" },
                    { id: "single", label: "Single events" },
                    { id: "series", label: "Series" },
                    { id: "trips", label: "Trips" },
                  ].map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setEventType(t.id)}
                      className={`px-3.5 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors ${
                        eventType === t.id
                          ? "bg-foreground text-background shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid={`event-type-${t.id}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Filters — Sport | City | Date | Submit */}
              <div className="flex flex-col sm:flex-row items-stretch">
              {/* Sport */}
              <div className="flex-1 border-b sm:border-b-0 sm:border-r border-border">
                <Select value={selectedSport} onValueChange={setSelectedSport}>
                  <SelectTrigger className="h-auto bg-transparent border-0 rounded-none px-4 py-3 sm:py-4 focus:ring-0 text-foreground" data-testid="hero-sport-select">
                    <div className="flex items-center gap-2 flex-1 min-w-0 text-left">
                      <Trophy className="w-4 h-4 text-muted-foreground shrink-0" />
                      <SelectValue placeholder="All Sports">
                        {SPORTS.find(s => s.id === selectedSport)?.label}
                      </SelectValue>
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {SPORTS.map((sport) => (
                      <SelectItem key={sport.id} value={sport.id}>
                        <span className="flex items-center gap-2">
                          <span>{sport.icon}</span>
                          <span>{sport.label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* City */}
              <div className="flex-1 border-b sm:border-b-0 sm:border-r border-border">
                <Select value={selectedCity || "all-cities"} onValueChange={(val) => setSelectedCity(val === "all-cities" ? "" : val)}>
                  <SelectTrigger className="h-auto bg-transparent border-0 rounded-none px-4 py-3 sm:py-4 focus:ring-0 text-foreground" data-testid="hero-city-select">
                    <div className="flex items-center gap-2 flex-1 min-w-0 text-left">
                      <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
                      <SelectValue placeholder="City">
                        {selectedCity || "All Cities"}
                      </SelectValue>
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-cities">🌍 All Cities</SelectItem>
                    {cities.map((city) => (
                      <SelectItem key={city} value={city}>{getCityWithFlag(city)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date */}
              <div className="flex items-center gap-2 px-4 py-3 sm:py-4 flex-1 border-b sm:border-b-0 sm:border-r border-border">
                <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
                <input
                  type={selectedDate ? "date" : "text"}
                  value={selectedDate}
                  onFocus={(e) => { e.target.type = "date"; try { e.target.showPicker && e.target.showPicker(); } catch (_) {} }}
                  onBlur={(e) => { if (!e.target.value) e.target.type = "text"; }}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  placeholder="Any date"
                  className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground"
                  data-testid="hero-date-input"
                />
                {selectedDate && (
                  <button
                    type="button"
                    onClick={() => setSelectedDate("")}
                    className="text-muted-foreground hover:text-foreground"
                    aria-label="Clear date"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Submit */}
              <Button
                onClick={() => {
                  const el = document.getElementById("games-list");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-none sm:rounded-r-2xl px-8 py-4 h-auto"
                data-testid="hero-search-btn"
              >
                Search
              </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Active filter badges (only shown when filters are applied) */}
      {(selectedSport !== "all" || selectedCity || selectedDate) && (
        <section className="container mx-auto px-4 pb-4">
          <div className="flex items-center gap-2 flex-wrap text-sm">
            <span className="text-muted-foreground">Filtering by:</span>
            {selectedSport !== "all" && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-card border border-border">
                {SPORTS.find(s => s.id === selectedSport)?.label}
                <button onClick={() => setSelectedSport("all")} className="ml-1 text-muted-foreground hover:text-foreground"><X className="w-3 h-3" /></button>
              </span>
            )}
            {selectedCity && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-card border border-border">
                <MapPin className="w-3 h-3" />
                {selectedCity}
                <button onClick={() => setSelectedCity("")} className="ml-1 text-muted-foreground hover:text-foreground"><X className="w-3 h-3" /></button>
              </span>
            )}
            {selectedDate && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-card border border-border">
                <Calendar className="w-3 h-3" />
                {new Date(selectedDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                <button onClick={() => setSelectedDate("")} className="ml-1 text-muted-foreground hover:text-foreground"><X className="w-3 h-3" /></button>
              </span>
            )}
            <button
              type="button"
              onClick={() => { setSelectedSport("all"); setSelectedCity(""); setSelectedDate(""); }}
              className="text-muted-foreground hover:text-foreground underline text-xs ml-2"
            >
              Clear all
            </button>
          </div>
        </section>
      )}

      {/* Trips & retreats row */}
      {(eventType === "all" || eventType === "trips") && (
        <TripsRow city={selectedCity} sport={selectedSport} />
      )}

      {/* Leagues & Series row */}
      {(eventType === "all" || eventType === "series") && (
        <LeaguesRow city={selectedCity} sport={selectedSport} />
      )}

      {/* Games Grid */}
      <main id="games-list" className="container mx-auto px-4 py-6">
        {(eventType === "all" || eventType === "single") && (
          <div className="flex items-center gap-2 mb-3">
            <CalendarDays className="w-4 h-4 text-primary" />
            <h2 className="font-display text-xl sm:text-2xl">Single events</h2>
          </div>
        )}
        {(() => {
          // Apply event-type + date filters client-side.
          // eventType "series" hides the single-events grid entirely (handled below).
          let displayedGames = games;
          if (eventType === "single") {
            displayedGames = displayedGames.filter(g => !g.series_id);
          }
          if (selectedDate) {
            displayedGames = displayedGames.filter(g => {
              const parts = g.date ? String(g.date).slice(0, 10) : "";
              return parts === selectedDate;
            });
          }
          if (eventType === "series" || eventType === "trips") {
            // Hide single events grid completely when user only wants series/trips
            return null;
          }
          return loading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="skeleton-pulse h-72 rounded-xl" />
            ))}
          </div>
        ) : displayedGames.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <Search className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-display text-xl mb-2">No games found</h3>
            <p className="text-muted-foreground text-sm">
              Try adjusting your filters or check back later
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="games-grid">
            {displayedGames.map((game, index) => (
              <button
                key={game.game_id}
                onClick={() => navigate(`/game/${game.game_id}`)}
                className="game-card glass-card rounded-xl overflow-hidden text-left group"
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`game-card-${game.game_id}`}
              >
                {/* Image */}
                <div className="relative h-40 overflow-hidden">
                  <img 
                    src={game.image_url} 
                    alt={game.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 card-image-overlay" />
                  
                  {/* Sport badge */}
                  <div className="absolute top-3 left-3">
                    <span className={`sport-badge ${getSportBadgeClass(game.sport)}`}>
                      {game.sport}
                    </span>
                  </div>
                  
                  {/* Spots left */}
                  <div className="absolute top-3 right-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      spotsLeft(game) <= 2 
                        ? "bg-destructive/90 text-white spots-urgent" 
                        : "bg-background/80 text-foreground"
                    }`}>
                      {spotsLeft(game)} spot{spotsLeft(game) !== 1 ? "s" : ""} left
                    </span>
                  </div>

                  {/* Series indicator */}
                  {game.series_id && (
                    <div className="absolute bottom-3 left-3">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/95 text-primary-foreground text-[10px] font-medium uppercase tracking-wider">
                        Part of a series
                      </span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-display text-lg font-semibold mb-2 line-clamp-1">
                    {game.title}
                  </h3>
                  
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-primary" />
                      <span>{formatDate(game.date)}</span>
                      <span className="text-border">•</span>
                      <Clock className="w-4 h-4" />
                      <span>{game.time_start} - {game.time_end}</span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-primary" />
                      <span className="line-clamp-1">{game.location_name}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-xs">
                      <span>{getCityWithFlag(game.city)}</span>
                    </div>
                    
                    <div className="flex items-center justify-between pt-2 border-t border-border/50 mt-2">
                      <div className="flex items-center gap-2">
                        {game.attendees_preview && game.attendees_preview.length > 0 ? (
                          <div className="flex items-center gap-2">
                            <AvatarStack attendees={game.attendees_preview} max={4} size="xs" />
                            <span className="text-xs">{game.current_players}/{game.max_players}</span>
                          </div>
                        ) : (
                          <>
                            <Users className="w-4 h-4" />
                            <span>{game.current_players}/{game.max_players}</span>
                          </>
                        )}
                      </div>
                      <span className={`font-semibold ${game.price === 0 ? "price-free" : "price-paid"}`}>
                        {formatPrice(game.price, game.currency)}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        );
        })()}
      </main>
    </div>
  );
};

export default HomePage;
